import './underwriting.subjectivity.list.detail.scss';

import {
  BindPageSubjectivity,
  NoteTargetAreaType,
  SubjectivityDeadline,
  SubmissionStatus,
  UploadFolderName,
  useUpdateQuoteSubjectivityMutation,
} from '@converge/graphql';
import {
  Box,
  CardContent,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  SelectChangeEvent,
  Stack,
  Typography,
  styled,
} from '@mui/material';
import {
  FileUploader,
  FormCheckBoxField,
  NewFromSelectField,
} from '../../../presentation';
import { FormProvider, useForm } from 'react-hook-form';
import { SetStateAction, useCallback, useEffect, useState } from 'react';
import { displayFormatDate, openInNewTab } from '@converge/utils';

import { ConvergeNotes } from '../../notes';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { useConvergeSnackBar } from '../../../contexts/use-snackbar';

import InsertCommentOutlinedIcon from '@mui/icons-material/InsertCommentOutlined';
const StyledPaper = styled(Paper)<{ attachedbelow?: string }>(
  ({ attachedbelow }) => ({
    boxShadow: 'none',
    border: '1px solid rgba(0, 0, 0, 0.12)',
    marginBottom: attachedbelow ? 0 : 20,
    ...(attachedbelow && {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      borderBottom: 'none',
    }),
  })
);
const StyledBox = styled(Box)(({ theme }) => ({
  padding: '25px',
  display: 'flex',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));
const StyledCard = styled(CardContent)(({ theme }) => ({
  padding: 0,
  flexBasis: '70%',
  [theme.breakpoints.down('md')]: {
    flexBasis: '100%',
  },
}));

interface IUnderwritingSubjectivityListDetailProps {
  submissionUuid: string;
  quoteUuid: string;
  subjectivity: BindPageSubjectivity;
  setIsLoading?: (loading: boolean) => void;
  isLoading?: boolean;
  status?: SubmissionStatus | null | undefined;
  onUpdate: (data: { isMet: boolean }) => void;
  setIsFetch: (value: SetStateAction<boolean>) => void;
  afterPolicy: boolean | null | undefined;
  disableSatisfied?: boolean;
  forceSatisfied?: boolean;
  attachedBelow?: boolean;
  hideWaived?: boolean;
  onSatisfied?: (checked: boolean) => Promise<void>;
}

export const UnderwritingSubjectivityListDetail = ({
  submissionUuid,
  quoteUuid,
  subjectivity,
  setIsLoading,
  isLoading,
  status,
  onUpdate,
  setIsFetch,
  afterPolicy,
  disableSatisfied = false,
  forceSatisfied = false,
  attachedBelow = false,
  hideWaived = false,
  onSatisfied,
}: IUnderwritingSubjectivityListDetailProps) => {
  const methods = useForm();
  const { deadline, createdAt, isSatisfied, waived } = subjectivity.quote ?? {};

  const [isNotesOpen, setIsNotesOpen] = useState<boolean>(false);
  const [isUploadOpen, setIsUploadOpen] = useState<boolean>(false);
  const [isWaivedClicked, setIsWaivedClicked] = useState<boolean>(!!waived);
  const [isSatisfiedClicked, setIsSatisfiedClicked] = useState<boolean>(
    !!isSatisfied
  );

  const { updateQuoteSubjectivity, error, loading } =
    useUpdateQuoteSubjectivityMutation();

  const { notify, closeSnackbar } = useConvergeSnackBar();

  const deadlineOptions = (status: SubmissionStatus | null | undefined) => {
    return Object.entries(SubjectivityDeadline)
      .map(([, label]) => {
        switch (label) {
          case SubjectivityDeadline.PriorToBind:
            return {
              value: label,
              label: 'Prior to bind',
              order: 0,
              disabled: status === SubmissionStatus.Bound ? true : false,
            };
          case SubjectivityDeadline.PriorToPolicy:
            return {
              value: label,
              label: 'Prior to policy issuance',
              order: 1,
            };
          case SubjectivityDeadline.AfterPolicy:
            return {
              value: label,
              label: 'After policy issuance',
              order: 2,
              disabled: !afterPolicy,
            };
          default:
            return {
              value: label,
              label,
              order: 3,
            };
        }
      })
      .sort((a, b) => a.order - b.order);
  };

  // Disabled subjectivites based on submission status and dropdown value
  const isDisabled = (option: null | string | undefined) => {
    return (
      (option === SubjectivityDeadline.PriorToBind &&
        (status === SubmissionStatus.Bound ||
          status === SubmissionStatus.PolicyIssued)) ||
      (option === SubjectivityDeadline.PriorToPolicy &&
        status === SubmissionStatus.PolicyIssued)
    );
  };

  useEffect(() => {
    setIsWaivedClicked(!!waived);
    setIsSatisfiedClicked(!!isSatisfied);
  }, [waived, isSatisfied]);

  useEffect(() => {
    if (disableSatisfied) {
      setIsSatisfiedClicked(false);
    }
  }, [disableSatisfied]);

  useEffect(() => {
    if (error) {
      notify({
        message: `Failed to update subjectivity: ${error}`,
        variant: 'error',
      });
    }
  }, [error, notify]);

  const handleSubjectivityUpdate = useCallback(
    async (event: SelectChangeEvent<SubjectivityDeadline | null>) => {
      const alert = notify(`Saving subjectivity...`, {
        variant: 'info',
      });
      try {
        setIsLoading?.(true);
        await updateQuoteSubjectivity({
          variables: {
            submissionUuid: submissionUuid,
            details: {
              subjectivityUuid: subjectivity?.uuid as string,
              deadline: event.target.value as SubjectivityDeadline,
            },
            quoteUuid,
            subjectivityUuid: subjectivity?.uuid as string,
          },
        });

        closeSnackbar(alert);
        setIsLoading?.(false);
        notify(`Saved subjectivity.`, {
          variant: 'success',
        });
        setIsFetch(true);
        onUpdate({
          isMet:
            subjectivity.quote?.isSatisfied ||
            subjectivity.quote?.waived ||
            deadline !== event.target.value ||
            false,
        });
      } catch (error) {
        closeSnackbar(alert);
        setIsLoading?.(false);
        notify(
          `Failed to save subjectivity: ${
            error instanceof Error ? error.message : 'unknown error'
          }`,
          {
            variant: 'error',
          }
        );
      }
    },
    [
      closeSnackbar,
      deadline,
      notify,
      onUpdate,
      quoteUuid,
      setIsFetch,
      setIsLoading,
      subjectivity.quote?.isSatisfied,
      subjectivity.quote?.waived,
      subjectivity?.uuid,
      submissionUuid,
      updateQuoteSubjectivity,
    ]
  );

  return (
    <FormProvider {...methods}>
      <StyledPaper attachedbelow={attachedBelow ? 'true' : undefined}>
        <StyledBox>
          <StyledCard>
            <Typography className="subjectivity-name" gutterBottom>
              {subjectivity?.name}
            </Typography>
            <Box className="issued-details">
              ISSUED{' '}
              {createdAt
                ? displayFormatDate(
                    createdAt,
                    displayFormatDate.DateFormatType.USADateFormatWithTimeAt
                  )
                : 'at an unknown date'}{' '}
            </Box>
            <NewFromSelectField
              name="deadline"
              sx={{ width: { xs: '100%', sm: '215px' } }}
              value={deadline}
              disabled={!!isDisabled(deadline)}
              onChange={handleSubjectivityUpdate}
              hideLabel
              options={deadlineOptions(status).map((item) => ({
                ...item,
                disabled: item.disabled || isDisabled(item.value),
              }))}
            />
            {subjectivity.quote?.attachments &&
            subjectivity.quote.attachments.edges.length > 0 ? (
              <Box>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: '#000000',
                    lineHeight: 1,
                    fontWeight: 600,
                    letterSpacing: 0.15,
                    marginTop: '7px',
                    marginBottom: '3px',
                  }}
                  gutterBottom
                >
                  Attachments
                </Typography>
                {subjectivity.quote?.attachments &&
                  subjectivity.quote.attachments.edges.map((attachment) => {
                    return (
                      <ListItem disablePadding>
                        <ListItemButton
                          onClick={() => {
                            if (attachment.node.url) {
                              openInNewTab(attachment.node.url);
                            }
                          }}
                          sx={{ paddingTop: '2px', paddingBottom: '2px' }}
                        >
                          <ListItemText
                            sx={{ margin: 0, lineHeight: 1 }}
                            primary={
                              <Typography component="span" fontSize="11px">
                                {
                                  attachment.node.filePath
                                    ?.split('/')
                                    .slice(-1)[0]
                                }
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
              </Box>
            ) : (
              false
            )}
          </StyledCard>
          <Box
            flexBasis="30%"
            justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
            display="flex"
            alignItems="center"
            mt={{ xs: 2, md: 0 }}
            className="subjectivities-satisfied"
          >
            <IconButton
              disabled={false}
              onClick={() => {
                setIsUploadOpen(!isUploadOpen);
                setIsNotesOpen(false);
              }}
              className="subjectivities-upload-icon-button"
            >
              <FileUploadIcon className="subjectivities-notes-icon" />
            </IconButton>
            <IconButton
              disabled={false}
              onClick={() => {
                setIsNotesOpen(!isNotesOpen);
                setIsUploadOpen(false);
              }}
              className="subjectivities-icon-button"
            >
              <InsertCommentOutlinedIcon className="subjectivities-notes-icon" />
            </IconButton>

            <div style={{ display: hideWaived ? 'none' : '' }}>
              <FormCheckBoxField
                defaultChecked={!!waived}
                name="waived"
                label="Waived"
                handleChange={async (event) => {
                  setIsWaivedClicked(!waived);
                  const activeVerb = waived ? 'Unwaiving' : 'Waiving';
                  const passiveVerb = waived ? 'Unwaived' : 'Waived';
                  const alert = notify(`${activeVerb} subjectivity.`, {
                    variant: 'info',
                  });

                  try {
                    setIsLoading?.(true);
                    await updateQuoteSubjectivity({
                      variables: {
                        submissionUuid: submissionUuid,
                        details: {
                          subjectivityUuid: subjectivity?.uuid as string,
                          waived: event.target.checked,
                        },
                        quoteUuid,
                        subjectivityUuid: subjectivity?.uuid as string,
                      },
                    });

                    closeSnackbar(alert);
                    setIsLoading?.(false);
                    notify(`${passiveVerb} subjectivity successfully.`, {
                      variant: 'success',
                    });
                    setIsFetch(true);
                    onUpdate({
                      isMet: event.target.checked,
                    });
                  } catch (error) {
                    setIsWaivedClicked(!!waived);
                    closeSnackbar(alert);
                    setIsLoading?.(false);
                    notify(
                      `${activeVerb} subjectivity failed: ${
                        error instanceof Error ? error.message : 'unknown error'
                      }`,
                      {
                        variant: 'error',
                      }
                    );
                  }
                }}
                checked={isWaivedClicked}
                disabled={
                  isLoading ||
                  loading ||
                  !!isSatisfied ||
                  !!isDisabled(deadline)
                }
              />
            </div>
            <FormCheckBoxField
              defaultChecked={!!isSatisfied || forceSatisfied}
              name="satisfied"
              label="Satisfied"
              handleChange={async (event) => {
                setIsSatisfiedClicked(!isSatisfiedClicked);
                const activeVerb = isSatisfied ? 'Unsatisfying' : 'Satisfying';
                const passiveVerb = isSatisfied ? 'Unsatisfied' : 'Satisfied';
                const alert = notify(`${activeVerb} subjectivity.`, {
                  variant: 'info',
                });

                try {
                  setIsLoading?.(true);

                  await updateQuoteSubjectivity({
                    variables: {
                      submissionUuid: submissionUuid,
                      details: {
                        subjectivityUuid: subjectivity?.uuid as string,
                        isSatisfied: event.target.checked,
                      },
                      quoteUuid,
                      subjectivityUuid: subjectivity?.uuid as string,
                    },
                  });

                  await onSatisfied?.(event.target.checked);
                  closeSnackbar(alert);
                  setIsLoading?.(false);
                  notify(`${passiveVerb} subjectivity successfully.`, {
                    variant: 'success',
                  });
                  setIsFetch(true);
                  onUpdate({
                    isMet: event.target.checked,
                  });
                } catch (error) {
                  setIsSatisfiedClicked(!!isSatisfied);
                  closeSnackbar(alert);
                  setIsLoading?.(false);
                  notify(
                    `${activeVerb} subjectivity failed: ${
                      error instanceof Error ? error.message : 'unknown error'
                    }`,
                    {
                      variant: 'error',
                    }
                  );
                }
              }}
              checked={isSatisfiedClicked || forceSatisfied}
              disabled={
                isLoading ||
                loading ||
                !!waived ||
                !!isDisabled(deadline) ||
                disableSatisfied
              }
            />
          </Box>
        </StyledBox>
        {isNotesOpen ? (
          <Stack padding={2}>
            <ConvergeNotes
              title="Notes"
              hideHeader
              targetArea={NoteTargetAreaType.Subjectivity}
              targetUuid={subjectivity?.quote?.uuid as string}
            />
          </Stack>
        ) : null}
        {isUploadOpen ? (
          <Stack padding={2}>
            <FileUploader
              folder={UploadFolderName.Subjectivities}
              label="Files"
              hiddenLabel
              onFileHandler={async (data) => {
                const alert = notify(`Uploading file..`, {
                  variant: 'info',
                });
                try {
                  await updateQuoteSubjectivity({
                    variables: {
                      submissionUuid: submissionUuid,
                      details: {
                        subjectivityUuid: subjectivity?.uuid as string,
                        attachments: {
                          files: data.acceptedFiles.map((file) => {
                            return {
                              file: file.file,
                              folder: file.folder,
                            };
                          }),
                        },
                      },
                      quoteUuid,
                      subjectivityUuid: subjectivity?.uuid as string,
                    },
                    context: {
                      fetchOptions: {
                        useUpload: true,
                        onProgress: (ev: ProgressEvent) => {
                          for (const file of data.acceptedFiles) {
                            data.onProgressChange(ev, file);
                          }
                        },
                      },
                    },
                  });

                  closeSnackbar(alert);
                  notify(`Successfully uploaded file.`, {
                    variant: 'success',
                  });
                } catch (error) {
                  closeSnackbar(alert);
                  notify(
                    `Failed to upload file: ${
                      error instanceof Error ? error.message : 'unknown error'
                    }`,
                    {
                      variant: 'error',
                    }
                  );
                }
              }}
            />
          </Stack>
        ) : null}
      </StyledPaper>
    </FormProvider>
  );
};
